import { useMutation, useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '../contexts/authContext';
import { artifactClient } from '../lib/connect/clients';
import { uploadAttachmentViaTesoro } from '../lib/uploadAttachment';
/**
 * Upload files as bare tenant artifacts via tesoro (presigned PUT direct to
 * MinIO), WITHOUT registering them on a zou project.
 *
 * This is the upload path for delivery system-of-record surfaces (program
 * documents, task attachments, deliverable evidence, score documents): the
 * record that owns the file lives in pangaea's delivery schema and references
 * the artifact id directly, so nothing about the upload may depend on the
 * program's backing contract project existing. Proposal-plane surfaces that
 * feed zou's extraction workspace (award intake, modification upload) keep
 * using useUploadProjectFiles instead.
 */ export function useUploadArtifacts() {
    const { user } = useAuth();
    return useMutation({
        mutationFn: async ({ files })=>{
            if (!user || !user.tenant_id) {
                throw new Error('Cannot upload: no authenticated user');
            }
            const tenantId = user.tenant_id;
            const ownerUserId = user.id;
            const settled = await Promise.allSettled(files.map((file)=>uploadAttachmentViaTesoro(file, {
                    tenantId,
                    ownerUserId,
                    autoProcess: false
                })));
            const result = {
                files: [],
                failed: []
            };
            settled.forEach((r, i)=>{
                if (r.status === 'fulfilled') {
                    result.files.push({
                        artifactId: r.value.artifactId,
                        name: r.value.filename,
                        file: files[i]
                    });
                } else {
                    result.failed.push({
                        name: files[i].name,
                        error: r.reason instanceof Error ? r.reason.message : String(r.reason),
                        file: files[i]
                    });
                }
            });
            return result;
        }
    });
}
/**
 * Resolve artifact ids to display filenames via the artifact plane
 * (tenant-gated GetArtifact per id, cached per id across the app).
 *
 * Replaces resolving names through a zou project's file list on delivery
 * surfaces. The fail-closed posture from PR #872 is preserved: an id whose
 * lookup fails (cross-tenant, deleted, transient error) simply never enters
 * the map, and callers render a non-clickable stub for unresolved ids.
 */ export function useArtifactNames(artifactIds) {
    // Key the query list by id CONTENT, not array identity. Callers typically
    // map ids from a query result inline, handing us a fresh array each render;
    // artifact ids never contain spaces, so a joined string is a faithful key.
    const idKey = [
        ...new Set(artifactIds.filter(Boolean))
    ].sort().join(' ');
    const ids = useMemo(()=>idKey ? idKey.split(' ') : [], [
        idKey
    ]);
    return useQueries({
        queries: ids.map((id)=>({
                queryKey: [
                    'artifactName',
                    id
                ],
                queryFn: async ()=>{
                    const resp = await artifactClient.getArtifact({
                        artifactId: id
                    });
                    return {
                        id,
                        name: resp.artifact?.filename ?? ''
                    };
                },
                staleTime: 5 * 60000,
                retry: false
            })),
        combine: (results)=>{
            const m = new Map();
            for (const r of results){
                if (r.data?.name) m.set(r.data.id, r.data.name);
            }
            return m;
        }
    });
}
